import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/components/auth/PermissionGate';
import { ConnectionStatusBadge } from '@/components/features/connection/ConnectionStatusBadge';
import { useAuth } from '@/contexts/AuthProvider';
import { currentFactoryIdAtom } from '@/stores/atoms';
import { hasAnyEffectivePermission } from '@/lib/permissions';
import { navItems, type NavItem } from './nav-items';

interface SidebarNavContentProps {
  isCollapsed: boolean;
  /** Called after a nav link is clicked — lets the mobile drawer close itself. */
  onNavigate?: () => void;
  /** Desktop-only collapse/expand rail; the mobile drawer has its own close affordance. */
  showCollapseToggle?: boolean;
  onToggleCollapse?: () => void;
}

export function SidebarNavContent({
  isCollapsed,
  onNavigate,
  showCollapseToggle = false,
  onToggleCollapse,
}: SidebarNavContentProps): React.JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const { currentTenantAccess, availableFactories } = useAuth();
  const currentFactoryId = useAtomValue(currentFactoryIdAtom);
  const currentFactory = availableFactories.find(f => f.id === currentFactoryId);

  const isActiveRoute = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const permissions = currentTenantAccess?.permissions;
  const isItemVisible = (item: NavItem): boolean =>
    !item.permission || (!!permissions && hasAnyEffectivePermission(permissions, item.permission));
  const visibleItems = navItems.filter(isItemVisible);

  return (
    <div className="flex h-full flex-col">
      {/* Brand — icon mark + product name + current factory */}
      <div
        className={cn('flex items-center gap-3 border-b p-4', isCollapsed && 'justify-center px-2')}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-fill shadow-tinted-md">
          <img src="/assets/procimo-pr-mark-white.png" alt="Procimo" className="h-6 w-auto" />
        </div>
        {!isCollapsed && (
          <div className="min-w-0">
            <div className="truncate text-[15px] font-bold leading-tight text-foreground">
              {t('navigation.app_name_short')}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {currentFactory?.name ?? t('factory.selector.selectFactory')}
            </div>
          </div>
        )}
      </div>

      {/* Sidebar Navigation */}
      <nav className="flex-1 overflow-y-auto p-2">
        <div className="space-y-1">
          {visibleItems.map(({ to, icon: Icon, labelKey, permission }) => {
            const isActive = isActiveRoute(to);
            const linkClassName = cn(
              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200',
              isActive && 'bg-accent-fill text-primary-foreground shadow-sm hover:shadow-md',
              !isActive &&
                'text-muted-foreground hover:bg-accent-bg hover:text-accent-text hover:shadow-sm',
              isCollapsed && 'justify-center'
            );

            const linkContent = (
              <>
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!isCollapsed && <span className="truncate">{t(labelKey)}</span>}
              </>
            );

            const link = (
              <Link
                to={to}
                className={linkClassName}
                title={isCollapsed ? t(labelKey) : undefined}
                onClick={onNavigate}
              >
                {linkContent}
              </Link>
            );

            if (Array.isArray(permission)) {
              return (
                <PermissionGate key={to} permissions={permission}>
                  {link}
                </PermissionGate>
              );
            }

            if (permission) {
              return (
                <PermissionGate key={to} permission={permission}>
                  {link}
                </PermissionGate>
              );
            }

            return <div key={to}>{link}</div>;
          })}
        </div>
      </nav>

      {/* Connection Status */}
      <div className="border-t px-2 py-1">
        <ConnectionStatusBadge isCollapsed={isCollapsed} />
      </div>

      {/* Collapse Toggle — desktop rail only */}
      {showCollapseToggle && (
        <div className="border-t bg-muted/50 p-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleCollapse}
            className={cn(
              'w-full justify-center text-muted-foreground transition-all hover:bg-muted hover:text-foreground hover:shadow-sm',
              !isCollapsed && 'justify-start'
            )}
            aria-label={
              isCollapsed ? t('navigation.expand_sidebar') : t('navigation.collapse_sidebar')
            }
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <>
                <ChevronLeft className="h-5 w-5" />
                <span className="ml-2 text-sm font-medium">{t('navigation.collapse')}</span>
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
